/**
 * Treatment cost estimates and the external-cost catalogue behind them:
 * the patient card's Estimates view, the builder, the issued document, its
 * PDFs and deliveries, and the `/finance/external-costs` catalogue page.
 */
export const estimates = {
  'estimate.send.routingPreferences': 'Delivery follows the patient’s communication preferences.',
  'estimate.status.draft': 'Draft',
  'estimate.status.issued': 'Issued',
  'estimate.source.clinic': 'Clinic service',
  'estimate.source.external': 'External cost',
  'estimate.template.clinicTreatment': 'Clinic treatment',
  'estimate.template.externalMedical': 'External medical services',
  'estimate.template.documentsAndLegal': 'Documents and legal',
  'estimate.template.travelAndTransfer': 'Travel and transfers',
  'estimate.template.accommodation': 'Accommodation',
  'estimate.template.dailyLiving': 'Daily living',
  'estimate.template.other': 'Other',
  'estimate.unit.item': 'item',
  'estimate.unit.service': 'service',
  'estimate.unit.day': 'day',
  'estimate.unit.night': 'night',
  'estimate.unit.trip': 'trip',
  'estimate.unit.hour': 'hour',
  'estimate.unit.week': 'week',
  'estimate.unit.person': 'person',
  'estimate.unit.package': 'package',
  'estimate.expiry.24h': '24 hours',
  'estimate.expiry.7d': '7 days',
  'estimate.expiry.30d': '30 days',
  'estimate.drift.unavailable': 'The source is no longer available; the line keeps its snapshot.',
  'estimate.drift.price': 'The catalogue price has changed since this line was taken.',
  'estimate.drift.revision': 'The catalogue has a newer revision of this source.',
  'estimate.error.issuedAlready': 'This estimate is already issued and cannot be changed.',
  'estimate.error.sourceUnavailable': 'One of the sources is no longer available.',
  'estimate.error.limit': 'The estimate exceeds a limit: 20 sections, 200 lines, quantity 1–9999.',
  'estimate.error.issueInvalid': 'The estimate cannot be issued as it stands.',
  'estimate.error.idempotency': 'This request was already sent with different content.',
  'estimate.error.forbidden': 'Your account may not do this.',
  'estimate.error.notFound': 'The estimate was not found.',
  'estimate.error.invalid': 'The server refused the estimate as sent.',
  'estimate.error.conflict': 'The estimate changed on the server.',
  'estimate.error.failed': 'The estimate could not be saved.',

  'estimate.list.title': 'Estimates',
  'estimate.list.column.title': 'Estimate',
  'estimate.list.column.locale': 'Language',
  'estimate.list.column.validThrough': 'Valid through',
  'estimate.list.column.total': 'Total',
  'estimate.list.column.when': 'Issued / updated',
  'estimate.list.column.actions': 'Actions',
  'estimate.list.noNumber': 'No number yet',
  'estimate.list.pendingDraft': 'Draft, revision {revision}',
  'estimate.list.openPendingDraft': 'Open the draft revision {revision} of {name}',
  'estimate.list.issuedAt': 'Issued {time}',
  'estimate.list.updatedAt': 'Updated {time}',
  'estimate.list.openDraft': 'Open draft {name}',
  'estimate.list.openIssued': 'Open estimate {name}',
  'estimate.list.create': 'New estimate',
  'estimate.list.empty': 'No estimates yet',
  'estimate.list.emptyDetail': 'Nothing has been estimated for this patient.',
  'estimate.list.emptyDetailWrite': 'Create the first estimate to plan the costs of treatment.',
  'estimate.list.noMatches': 'No estimates match',
  'estimate.list.noMatchesDetail': 'Loosen the filters to see more estimates.',
  'estimate.list.loading': 'Loading estimates…',
  'estimate.list.loadFailed': 'The estimates could not be loaded.',
  'estimate.list.forbidden': 'Estimates are not available to your account',
  'estimate.list.forbiddenDetail': 'Reading treatment estimates needs the finance.estimates.read capability.',

  'estimate.create.title': 'New estimate',
  'estimate.create.description': 'The header first; the lines are picked in the builder.',
  'estimate.create.defaultTitle': 'Treatment cost estimate',
  'estimate.create.cancel': 'Cancel',
  'estimate.create.confirm': 'Create',
  'estimate.create.creating': 'Creating…',
  'estimate.fields.documentLocale': 'Document language',
  'estimate.fields.title': 'Title',
  'estimate.fields.planningDate': 'Pricing date',
  'estimate.fields.validThrough': 'Valid through',
  'estimate.fields.plannedStart': 'Planned start',
  'estimate.fields.plannedEnd': 'Planned end',
  'estimate.fields.case': 'Case',
  'estimate.fields.noCase': 'No case',
  'estimate.fields.assumptions': 'Assumptions',

  'estimate.validation.title': 'A title is required.',
  'estimate.validation.planningDate': 'A pricing date is required for new tariff selection.',
  'estimate.validation.source': 'Select a dated price list and branch for this service.',
  'estimate.validation.validThrough': 'A validity date is required.',
  'estimate.validation.plannedRange': 'The planned end must not be before the planned start.',
  'estimate.validation.customTitleBlank': 'A custom section needs a name.',
  'estimate.validation.customTitleDuplicate': 'Two sections cannot share a name.',
  'estimate.validation.templateDuplicate': 'Each template can be used once.',
  'estimate.validation.sectionLimit': 'At most 20 sections.',
  'estimate.validation.lineLimit': 'At most 200 lines.',
  'estimate.validation.quantity': 'Quantity must be between {min} and {max}.',
  'estimate.validation.quantityShort': 'Quantity {min}–{max}',
  'estimate.validation.amount': 'A price is required.',
  'estimate.validation.overrideReason': 'An overridden price needs a reason.',
  'estimate.validation.emptySection': 'Section “{name}” has no lines.',
  'estimate.validation.emptySections': 'Every section needs at least one line before issuing.',
  'estimate.validation.limits': 'Sections: {sections} of 20 · Lines: {lines} of 200',
  'estimate.validation.noLines': 'Pick at least one line before issuing.',

  'estimate.section.untitled': 'Untitled section',
  'estimate.section.subtotal': 'Subtotal {amount}',

  'estimate.totals.label': 'Totals',
  'estimate.totals.clinic': 'Clinic services',
  'estimate.totals.external': 'External costs',
  'estimate.totals.grand': 'Grand total',
  'estimate.totals.serverNote': 'As last saved on the server.',
  'estimate.totals.localNote': 'Changed amounts are confirmed when the draft is saved.',
  'estimate.totals.pending': 'Awaiting save',
  'estimate.grid.label': 'Estimate lines',
  'estimate.grid.column.name': 'Item',
  'estimate.grid.column.quantity': 'Quantity',
  'estimate.grid.column.unit': 'Unit',
  'estimate.grid.column.price': 'Unit price',
  'estimate.grid.column.total': 'Total',
  'estimate.grid.empty': 'No lines yet',
  'estimate.grid.emptyDetail':
    'Pick services and external costs from the panel; each is filed under its catalogue group.',

  'estimate.conflict.title': 'The estimate changed on the server',
  'estimate.conflict.description': 'Someone saved this estimate after you opened it.',
  'estimate.conflict.descriptionRevision':
    'The server holds revision {revision}; your edits were made against an older one.',
  'estimate.conflict.keepEditing': 'Keep editing',
  'estimate.conflict.reload': 'Discard my changes and reload',
  'estimate.conflict.reapply': 'Apply my changes to the latest',
  'estimate.conflict.reapplying': 'Applying…',
  'estimate.conflict.loading': 'Comparing with the server…',
  'estimate.conflict.loadFailed': 'The server revision could not be loaded.',
  'estimate.conflict.summaryLabel': 'What differs',
  'estimate.conflict.headerChanged': 'Header fields changed: {count}',
  'estimate.conflict.sectionsOnlyLocal': 'Sections only in my draft: {count}',
  'estimate.conflict.sectionsOnlyServer': 'Sections only on the server: {count}',
  'estimate.conflict.linesOnlyLocal': 'Lines only in my draft: {count}',
  'estimate.conflict.linesOnlyServer': 'Lines only on the server: {count}',
  'estimate.conflict.linesChanged': 'Lines changed in both: {count}',
  'estimate.conflict.noDifferences': 'No differences in content.',
  'estimate.conflict.reloadTitle': 'Discard your changes?',
  'estimate.conflict.reloadDescription': 'Your unsaved edits will be replaced by the server revision.',
  'estimate.conflict.reloadConfirm': 'Discard and reload',

  'estimate.issue.title': 'Issue this estimate',
  'estimate.issue.description': 'Issuing freezes the content, assigns a number and generates the PDF.',
  'estimate.issue.patient': 'Patient',
  'estimate.issue.missing': 'Missing',
  'estimate.issue.structure': 'Structure',
  'estimate.issue.structureValue': 'Sections: {sections} · Lines: {lines}',
  'estimate.issue.blocked': 'Fix these before issuing',
  'estimate.issue.cancel': 'Cancel',
  'estimate.issue.confirm': 'Issue and generate PDF',
  'estimate.issue.issuing': 'Issuing…',
  'estimate.disclaimer':
    'This is an estimate, not an invoice. External costs are indicative and payable to third parties.',

  'estimate.pdf.title': 'PDF documents',
  'estimate.pdf.rendering': 'Generating the PDF…',
  'estimate.pdf.retry': 'Retry PDF',
  'estimate.pdf.loadingArtifacts': 'Loading documents…',
  'estimate.pdf.artifactsFailed': 'The documents could not be loaded.',
  'estimate.pdf.none': 'No PDF has been generated yet.',
  'estimate.pdf.renderer': 'Renderer {version} · {hash}',
  'estimate.pdf.download': 'Download',
  'estimate.pdf.view': 'View',
  'estimate.pdf.viewNamed': 'View the {language} PDF',
  'estimate.pdf.downloadFailed': 'The PDF could not be downloaded.',
  'estimate.pdf.generate': 'Generate PDF',
  'estimate.pdf.forbidden': 'Your account may not generate this PDF.',
  'estimate.pdf.unavailable': 'No print form is bound for this document language.',
  'estimate.pdf.conflict': 'The PDF was already generated for this request.',
  'estimate.pdf.failed': 'The PDF could not be generated.',

  'estimate.send.open': 'Send to patient',
  'estimate.send.needsPdf': 'Generate the PDF first.',
  'estimate.send.title': 'Send to patient',
  'estimate.send.description': 'The patient receives a link to the PDF; the link expires.',
  'estimate.send.artifact': 'Document',
  'estimate.send.artifactOption': '{locale} · {time}',
  'estimate.send.expiry': 'Link expires in',
  'estimate.send.preview': 'Message preview',
  'estimate.send.previewBody':
    'Dear patient, your treatment cost estimate “{title}” {number} is ready. The estimate is valid through {validThrough}. The link to the document expires in {expiry}.',
  'estimate.send.cancel': 'Cancel',
  'estimate.send.confirm': 'Send',
  'estimate.send.sending': 'Sending…',
  'estimate.send.done': 'Close',
  'estimate.send.result': 'Delivery: {state}',

  'estimate.delivery.title': 'Deliveries',
  'estimate.delivery.loading': 'Loading deliveries…',
  'estimate.delivery.loadFailed': 'The deliveries could not be loaded.',
  'estimate.delivery.none': 'Not sent yet.',
  'estimate.delivery.column.channel': 'Channel',
  'estimate.delivery.column.artifact': 'Document',
  'estimate.delivery.column.state': 'State',
  'estimate.delivery.column.times': 'Timeline',
  'estimate.delivery.column.link': 'Link',
  'estimate.delivery.column.actions': 'Actions',
  'estimate.delivery.createdAt': 'Sent {time}',
  'estimate.delivery.expiresAt': 'Expires {time}',
  'estimate.delivery.revokedAt': 'Revoked {time}',
  'estimate.delivery.linkActive': 'Link active',
  'estimate.delivery.linkExpired': 'Link expired',
  'estimate.delivery.linkRevoked': 'Link revoked',
  'estimate.delivery.downloads': 'Downloads: {count}',
  'estimate.delivery.firstAccess': 'First opened {time}',
  'estimate.delivery.lastAccess': 'Last opened {time}',
  'estimate.delivery.revoke': 'Revoke link',
  'estimate.delivery.revokeTitle': 'Revoke this link?',
  'estimate.delivery.revokeDescription':
    'The patient will no longer be able to open the document through it. A new send is the only way to give a link again.',
  'estimate.delivery.revokeCancel': 'Keep link',
  'estimate.delivery.revokeConfirm': 'Revoke',
  'estimate.delivery.revoking': 'Revoking…',

  'estimate.revision.open': 'Edit',
  'estimate.revision.title': 'Edit this estimate?',
  'estimate.revision.description':
    'An editable copy of {number} (revision {revision}) is created as a new draft; this document stays as issued.',
  'estimate.revision.cancel': 'Cancel',
  'estimate.revision.confirm': 'Create the editable copy',
  'estimate.revision.creating': 'Creating…',

  'estimate.delete.open': 'Delete draft',
  'estimate.delete.title': 'Delete this draft?',
  'estimate.delete.description':
    'The draft {name} and everything picked into it are removed for good. Nothing that has been issued is touched.',
  'estimate.delete.cancel': 'Keep draft',
  'estimate.delete.confirm': 'Delete the draft',
  'estimate.delete.deleting': 'Deleting…',

  'estimate.revisions.column.status': 'Status',
  'estimate.revisions.column.number': 'Number',
  'estimate.revisions.column.when': 'Issued / updated',
  'estimate.revisions.column.total': 'Total',
  'estimate.revisions.open': 'Open revision {revision}',
  'estimate.revisions.reading': 'Revision {revision}, open now',
  'estimate.revisions.loadFailed': 'The revision history could not be loaded.',

  'estimate.detail.loading': 'Loading the estimate…',
  'estimate.detail.notFound': 'This estimate does not exist for this patient.',
  'estimate.detail.forbidden': 'Your account may not read this estimate.',
  'estimate.detail.loadFailed': 'The estimate could not be loaded.',
  'estimate.detail.readOnlyDraft': 'This draft is read-only for your account.',
  'estimate.detail.planned': 'Planned period',
  'estimate.detail.issuedAt': 'Issued',
  'estimate.detail.updatedAt': 'Updated',
  'estimate.detail.factsLabel': 'Document details',
  'estimate.detail.overrideNote': 'Price overridden: {reason}',

  'estimate.builder.untitled': 'Untitled estimate',
  'estimate.builder.backToList': 'All estimates',
  'estimate.builder.updatedAt': 'Updated {time}',
  'estimate.builder.actionsLabel': 'Estimate actions',
  'estimate.builder.saving': 'Saving…',
  'estimate.builder.unsaved': 'Unsaved changes',
  'estimate.builder.saved': 'Saved',
  'estimate.builder.detailsLabel': 'Estimate details',
  'estimate.builder.pick': 'Add items',
  'estimate.builder.save': 'Save',
  'estimate.builder.issue': 'Issue and generate PDF',
  'estimate.builder.sectionLimit': 'Sections: {limit} at most',
  'estimate.builder.lineLimit': 'Lines: {limit} at most',
  'estimate.builder.sectionLimitPick':
    '{name} needs a new section, {section}; the estimate already has {limit} sections.',
  'estimate.builder.alreadyPresent': '{name} is already in {section}.',
  'estimate.builder.added': '{name} added to {section}.',
  'estimate.builder.refreshUnavailable': '{name} is no longer in the catalogue; the line keeps its snapshot.',
  'estimate.builder.refreshed': '{name} refreshed from the catalogue.',

  'estimate.line.priceLocked': 'Clinic price, from the price list',
  'estimate.line.overrideReason': 'Reason for overriding {amount}',
  'estimate.line.note': 'Note',
  'estimate.line.noteLabel': 'Note for {name}',
  'estimate.line.refresh': 'Refresh {name} from the catalogue',
  'estimate.line.refreshHint': 'Take the current price and name',
  'estimate.line.remove': 'Remove {name} from {section}',
  'estimate.line.quantityLabel': 'Quantity of {name} ({unit})',
  'estimate.line.decrease': 'Decrease {name}',
  'estimate.line.increase': 'Increase {name}',
  'estimate.line.unitPrice': 'Unit price of {name}',

  'estimate.remove.lineTitle': 'Remove this line?',
  'estimate.remove.lineDescription': '“{name}” carries a note or an overridden price; removing it drops them.',
  'estimate.remove.cancel': 'Keep',
  'estimate.remove.confirm': 'Remove',
  'estimate.refresh.title': 'Refresh {name}?',
  'estimate.refresh.description': 'The catalogue has changed since this line was taken.',
  'estimate.refresh.amount': 'Price: {from} → {to}',
  'estimate.refresh.display': 'Name: {from} → {to}',
  'estimate.refresh.cancel': 'Keep as is',
  'estimate.refresh.confirm': 'Refresh',

  'estimate.picker.title': 'Services and external costs',
  'estimate.picker.priceList': 'Price list',
  'estimate.picker.choosePriceList': 'Select a price list',
  'estimate.picker.branch': 'Branch',
  'estimate.picker.chooseBranch': 'Select a branch',
  'estimate.picker.selectionRequired': 'Select a price list and branch.',
  'estimate.picker.selectionLoading': 'Loading price lists and branches.',
  'estimate.picker.selectionFailed': 'Price lists or branches could not be loaded.',
  'estimate.picker.datedForbidden': 'You do not have access to price lists.',
  'estimate.picker.resolving': 'Checking the price for the planning date.',
  'estimate.picker.resolveFailed': 'The price could not be resolved for this service, branch and planning date. No item was added.',
  'estimate.picker.currencyMismatch': 'This source has a different currency definition from the estimate.',
  'estimate.picker.baseTariff': 'Base tariff: {amount}',
  'estimate.picker.catalogueTab': 'Catalogue',
  'estimate.picker.searchPlaceholder': 'Search by code or name',
  'estimate.picker.hint': 'A pick is filed under its catalogue group.',
  'estimate.picker.shortlistMissing': '{name} is not in the price list.',
  'estimate.picker.clinicHint': 'Clinic services from the price list',
  'estimate.picker.externalHint': 'External costs from the catalogue',
  'estimate.picker.noMatches': 'Nothing matches.',
  'estimate.picker.clinicLeafMeta': '{unit}',
  'estimate.picker.externalLeafMeta': '{path} · {unit}',
  'estimate.picker.externalLoading': 'Loading the external-cost catalogue…',
  'estimate.picker.externalFailed': 'The external-cost catalogue could not be loaded.',
  'estimate.picker.externalEmpty': 'The external-cost catalogue is empty.',
  'estimate.picker.lineLimit': 'The estimate already has 200 lines.',
  'estimate.unsavedIntent': 'an estimate with unsaved changes',

  'externalCosts.title': 'External costs',
  'externalCosts.subtitle': 'Third-party expenses an estimate can quote beside clinic services',
  'externalCosts.forbidden': 'The external-cost catalogue is not available to your account.',
  'externalCosts.loading': 'Loading the catalogue…',
  'externalCosts.loadFailed': 'The catalogue could not be loaded.',
  'externalCosts.tableLabel': 'External-cost catalogue',
  'externalCosts.column.name': 'Name',
  'externalCosts.column.group': 'Group',
  'externalCosts.column.unit': 'Unit',
  'externalCosts.column.amount': 'Default price',
  'externalCosts.column.status': 'Status',
  'externalCosts.actions': 'Actions',
  'externalCosts.editNamed': 'Edit {name}',
  'externalCosts.openNamed': 'Open {name}',
  'externalCosts.openGroupNamed': 'Open the group {name}',
  'externalCosts.moveSelected': 'Move {count} selected',
  'externalCosts.moveSelectedTitle': 'Move to another group',
  'externalCosts.moveSelectedDescription':
    '{count} catalogue entries go to the end of the group you choose, in the order they stand in the tree.',
  'externalCosts.moveSelectedTarget': 'Destination',
  'externalCosts.moveSelectedHint': 'Only groups that can hold the whole selection are listed.',
  'externalCosts.moveSelectedConfirm': 'Move {count} entries',
  'externalCosts.moveSelectedCancel': 'Keep the selection',
  'externalCosts.movedCount': '{count} entries were moved.',
  'externalCosts.movedBefore': '{count} entries were moved before that.',
  'externalCosts.empty': 'No external costs',
  'externalCosts.emptyDetail': 'Nothing has been added yet.',
  'externalCosts.emptyDetailManage': 'Add one — a group keeps several of them together.',
  'externalCosts.noMatches': 'Nothing matches.',
  'externalCosts.noMatchesDetail': 'Clear a filter to see more of the catalogue.',
  // One step of a catalogue path joined to the next. A path is a string —
  // an option label, a message parameter — so the mark cannot be hidden
  // markup and belongs to the translator instead.
  'externalCosts.pathStep': '{parent} / {child}',
  'externalCosts.archived': 'Archived',
  'externalCosts.archivedByParent': 'Under an archived group',
  'externalCosts.status.active': 'Active',
  'externalCosts.saved': 'Saved.',
  'externalCosts.unsavedIntent': 'an external-cost node with unsaved changes',
  'externalCosts.kind.group': 'Group',
  'externalCosts.kind.item': 'Item',
  'externalCosts.groups.open': 'Groups',
  'externalCosts.groups.title': 'Groups',
  'externalCosts.tree.search': 'Search the catalogue',
  'externalCosts.tree.root': 'Whole catalogue',
  'externalCosts.tree.showAll': 'Everything',
  'externalCosts.tree.showGroups': 'Groups only',
  'externalCosts.tree.showItems': 'Items only',
  'externalCosts.tree.empty': 'No groups or items yet.',
  'externalCosts.tree.levelEmpty': 'Nothing is filed here yet.',
  'externalCosts.tree.levelItemsOnly': 'Only items are filed here, and the fold shows groups.',
  'externalCosts.tree.unfold': 'Show everything',
  'externalCosts.tree.noMatches': 'Nothing in the catalogue matches.',
  'externalCosts.tree.scopeChip': 'In {name}',
  'externalCosts.tree.scopeClear': 'Show the whole catalogue',
  'externalCosts.tree.menu': 'Actions for {name}',
  'externalCosts.tree.open': 'Open',
  'externalCosts.tree.rename': 'Rename',
  'externalCosts.tree.addGroup': 'Add a group here',
  'externalCosts.tree.addItem': 'Add an item here',
  'externalCosts.tree.moveEarlier': 'Move earlier',
  'externalCosts.tree.moveLater': 'Move later',
  'externalCosts.tree.moveToTop': 'Move to the top level',
  'externalCosts.tree.stale': 'The catalogue changed meanwhile and was reloaded. Try again.',
  'externalCosts.create.under': 'Under {name}',
  'externalCosts.create.kind': 'What to add',
  'externalCosts.create.groupHint': 'A heading that holds items',
  'externalCosts.create.itemHint': 'A priced expense with a unit',
  'externalCosts.create.submit': 'Create',
  'externalCosts.create.creating': 'Creating…',
  'externalCosts.detail.archive': 'Archive',
  'externalCosts.detail.restore': 'Restore',
  'externalCosts.detail.noAmount': 'No default price',
  'externalCosts.editor.newGroup': 'New group',
  'externalCosts.editor.newItem': 'New item',
  'externalCosts.editor.name': 'Name',
  'externalCosts.editor.description': 'Description',
  'externalCosts.editor.unit': 'Unit',
  'externalCosts.editor.unitNone': 'No unit',
  'externalCosts.editor.amount': 'Default price, {currency}',
  'externalCosts.editor.amountUnknown': 'Default price',
  'externalCosts.error.currencyUnavailable': 'The financial profile currency is unavailable. Catalogue entries cannot be saved.',
  'externalCosts.error.currencyMismatch': 'This price uses a different currency definition from the current financial profile. Create a new item to use the current currency.',
  'externalCosts.editor.group': 'Group',
  'externalCosts.editor.groupHint': 'The catalogue keeps one level of groups.',
  'externalCosts.editor.rootParent': 'Top level',
  'externalCosts.editor.save': 'Save',
  'externalCosts.editor.saving': 'Saving…',
  'externalCosts.editor.gone': 'This node is no longer in the catalogue.',
  'externalCosts.editor.conflict':
    'The catalogue changed since you opened this form. Reload it — your entries stay — and save again.',
  'externalCosts.editor.reload': 'Reload catalogue',
  'externalCosts.editor.reloading': 'Reloading…',
  'externalCosts.editor.discard': 'Discard my changes',
  'externalCosts.validation.name': 'A name is required.',
  'externalCosts.validation.unit': 'A unit is required for an item.',
  'externalCosts.validation.amount': 'Enter a non-negative price with the currency\'s exact decimal precision.',
  'externalCosts.error.codeConflict': 'The catalogue could not settle this entry’s internal code. Try again.',
  'externalCosts.error.cycle': 'A group cannot be moved under itself.',
  'externalCosts.error.parentUnavailable': 'The chosen parent is gone or archived.',
  'externalCosts.error.depthExceeded': 'The tree allows six levels at most.',
  'externalCosts.error.forbidden': 'Your account may not change the catalogue.',
  'externalCosts.error.invalid': 'The server refused the node as sent.',
  'externalCosts.error.conflict': 'The catalogue refused the change.',
  'externalCosts.error.failed': 'The catalogue could not be changed.',
  'externalCosts.archive.title': 'Archive {name}?',
  'externalCosts.archive.groupDescription':
    'The group will no longer be offered to new estimate lines, nor will the nodes inside it: {count}. Existing lines keep their snapshots.',
  'externalCosts.archive.itemDescription':
    'The item will no longer be offered to new estimate lines. Existing lines keep their snapshots.',
  'externalCosts.archive.cancel': 'Keep',
  'externalCosts.archive.confirm': 'Archive',
  'externalCosts.archive.archiving': 'Archiving…',
} as const
